import { useEffect, useRef, useState } from 'react';

const painCards = [
  {
    icon: 'ri-close-circle-line',
    iconColor: '#DC5014',
    borderColor: '#DC5014',
    title: 'You Lose High-Ticket Contracts',
    body:
      'The $80,000 client signs with whoever looks and operates like a corporation — not with the best craftsman. Without a brand, a showroom, and a commercial team, you\'re invisible to the clients who pay the most.',
    tag: 'LOST REVENUE',
  },
  {
    icon: 'ri-user-unfollow-line',
    iconColor: '#DC5014',
    borderColor: '#DC5014',
    title: 'You\'re Selling. Not Building.',
    body:
      'Every hour you spend chasing leads, writing proposals, and following up with clients is an hour you\'re not on the job. You didn\'t become a contractor to be a salesperson. But without sales, there\'s no work.',
    tag: 'LOST TIME',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    iconColor: '#DC5014',
    borderColor: '#DC5014',
    title: 'Your Pipeline Is Unpredictable',
    body:
      'Feast or famine. One month you\'re overwhelmed, the next you\'re desperate for work. Without a structured commercial operation, your revenue will always be at the mercy of referrals and luck.',
    tag: 'LOST STABILITY',
  },
];

export default function PainSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="pain"
      ref={ref}
      className="relative overflow-hidden"
      style={{ background: '#FFFFFF', padding: '100px 80px' }}
    >
      {/* Subtle top border accent */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{ height: '3px', background: 'linear-gradient(to right, transparent, #DC5014 30%, #DC5014 70%, transparent)' }}
      />

      <div className="max-w-6xl mx-auto">
        {/* Eyebrow */}
        <div
          className={`flex items-center gap-4 mb-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div style={{ width: '32px', height: '2px', background: '#DC5014' }} />
          <p
            className="font-sans font-bold uppercase"
            style={{ color: '#DC5014', fontSize: '11px', letterSpacing: '0.32em' }}
          >
            THE REAL PROBLEM
          </p>
        </div>

        {/* Headline */}
        <div
          className={`mb-8 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <h2
            className="font-playfair"
            style={{
              fontSize: 'clamp(32px, 4vw, 56px)',
              color: '#0A0A0A',
              fontWeight: 400,
              lineHeight: 1.15,
              maxWidth: '680px',
            }}
          >
            You&apos;re Great at What You Do.
            <br />
            <span style={{ fontStyle: 'italic', color: 'rgba(10,10,10,0.45)' }}>
              But Nobody Knows It.
            </span>
          </h2>
        </div>

        {/* Sub */}
        <div
          className={`mb-16 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p
            className="font-sans"
            style={{
              fontSize: '17px',
              color: '#555',
              lineHeight: 1.9,
              maxWidth: '600px',
              fontWeight: 300,
            }}
          >
            Every year, skilled contractors lose six-figure contracts — not because
            of bad work, but because they show up without a brand, without a showroom,
            without a commercial team behind them.
            <br /><br />
            The $80,000 client signs with whoever looks and operates like a corporation.
          </p>
        </div>

        {/* 3 Pain Cards */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {painCards.map((card, i) => (
            <div
              key={card.title}
              className="relative flex flex-col"
              style={{
                background: '#FFFFFF',
                border: '1px solid #F0F0F0',
                borderTop: `3px solid ${card.borderColor}`,
                borderRadius: '0',
                padding: '32px 28px',
                transition: `all 0.3s ease ${i * 80}ms`,
              }}
            >
              {/* Tag */}
              <p
                className="font-sans font-bold uppercase mb-5"
                style={{ color: 'rgba(220,80,20,0.5)', fontSize: '10px', letterSpacing: '0.28em' }}
              >
                {card.tag}
              </p>

              {/* Icon */}
              <div
                className="flex items-center justify-center mb-5 flex-shrink-0"
                style={{ width: '48px', height: '48px' }}
              >
                <i className={card.icon} style={{ color: card.iconColor, fontSize: '28px' }} />
              </div>

              {/* Title */}
              <h3
                className="font-sans font-semibold mb-4"
                style={{ color: '#0A0A0A', fontSize: '16px', lineHeight: 1.35 }}
              >
                {card.title}
              </h3>

              {/* Body */}
              <p
                className="font-sans"
                style={{ color: '#777', fontSize: '14px', lineHeight: 1.8, fontWeight: 300 }}
              >
                {card.body}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div
          className={`mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{
            borderTop: '1px solid #F0F0F0',
            paddingTop: '40px',
          }}
        >
          <div>
            <p
              className="font-playfair italic"
              style={{ color: '#0A0A0A', fontSize: '22px', lineHeight: 1.4, maxWidth: '480px' }}
            >
              &ldquo;The best contractors in America don&apos;t lose because of their craft.
              They lose because of their commercial structure.&rdquo;
            </p>
            <p
              className="font-sans mt-3"
              style={{ color: '#DC5014', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em' }}
            >
              — BARKFY COMMERCIAL TEAM
            </p>
          </div>
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="font-sans font-bold whitespace-nowrap cursor-pointer transition-all duration-300 hover:brightness-110 active:scale-95 flex-shrink-0"
            style={{
              background: '#DC5014',
              color: '#F0F0F0',
              padding: '16px 36px',
              borderRadius: '6px',
              fontSize: '13px',
              letterSpacing: '0.06em',
            }}
          >
            FIX THIS NOW →
          </button>
        </div>
      </div>
    </section>
  );
}
